import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Select from "react-select";
import axiosInstance from "../../../../../services/AxiosInstance";

const MedicineDropdown = ({ value, onChange, isMulti = false }) => {
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedMedicineLabel, setSelectedMedicineLabel] = useState(null);

  const searchTimeoutRef = useRef(null);
  const isFetchingRef = useRef(false);
  const initialFetchRef = useRef(false);
  const pageSize = 10;

  const fetchMedicines = useCallback(async (pageNo, searchText = "", isReset = false) => {
    if (isFetchingRef.current) return;
    
    try {
      isFetchingRef.current = true;
      if (isReset) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const res = await axiosInstance.post("/admin/medicineList", {
        pagination: true,
        pageid: pageNo,
        search_by: "medicine_name",
        search_keyword: searchText || "",
      });

      const result = res.data;

      if (result?.status === "success") {
        const newData = result.data || [];
        const numRows = result.num_rows || 0;
        
        const totalPages = newData.length > 0 ? Math.ceil(numRows / pageSize) : 0;
        const currentPageIndex = pageNo;
        const hasMorePages = currentPageIndex < (totalPages - 1);

        const formatted = newData.map((item) => ({
          value: String(item.medicine_id),
          label: item.medicine_name || item.display_name_en || `Medicine ${item.medicine_id}`,
        }));

        if (isReset) {
          setOptions(formatted);
          setPage(0);
        } else {
          setOptions((prev) => {
            const existingIds = new Set(prev.map(opt => opt.value));
            const uniqueNew = formatted.filter(opt => !existingIds.has(opt.value));
            return [...prev, ...uniqueNew];
          });
          setPage(pageNo);
        }

        setHasMore(hasMorePages);
      }
    } catch (err) {
      console.error("[v0] Medicine fetch error:", err.message);
    } finally {
      isFetchingRef.current = false;
      if (isReset) {
        setLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  }, []);

  const fetchMedicineLabel = useCallback(async (medicineId) => {
    try {
      const res = await axiosInstance.post("/admin/medicineList", {
        pagination: false,
        search_by: "medicine_id",
        search_keyword: String(medicineId),
      });

      if (res.data?.status === "success" && res.data?.data?.length > 0) {
        const medicine = res.data.data[0];
        const label = medicine.medicine_name || medicine.display_name_en || `Medicine ${medicineId}`;
        setSelectedMedicineLabel(label);
      }
    } catch (err) {
      console.error("[v0] Error fetching medicine label:", err.message);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (!initialFetchRef.current && !isInitialized) {
      initialFetchRef.current = true;
      fetchMedicines(0, "", true);
      setIsInitialized(true);
    }
  }, []);

  // Fetch label for edit mode
  useEffect(() => {
    if (value) {
      const valueArray = Array.isArray(value) ? value : [value];
      const firstValue = valueArray[0];
      
      if (typeof firstValue === 'string' || (firstValue?.value && !firstValue?.label)) {
        const medicineId = typeof firstValue === 'string' ? firstValue : firstValue.value;
        fetchMedicineLabel(medicineId);
      }
    }
  }, [value, fetchMedicineLabel]);

  // Search with debounce
  const handleInputChange = useCallback((inputValue) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearch(inputValue);
      setPage(0);
      fetchMedicines(0, inputValue, true);
    }, 500);
  }, [fetchMedicines]);

  // Load more on scroll
  const handleMenuScrollToBottom = useCallback(() => {
    if (hasMore && !isLoadingMore && !loading && !isFetchingRef.current) {
      const nextPage = page + 1;
      fetchMedicines(nextPage, search, false);
    }
  }, [hasMore, isLoadingMore, loading, page, search, fetchMedicines]);

  // Display value
  const selectedValue = useMemo(() => {
    if (!value) {
      return isMulti ? [] : null;
    }

    const valueArray = Array.isArray(value) ? value : [value];
    
    if (valueArray.length === 0) {
      return isMulti ? [] : null;
    }

    // If values already have labels
    if (valueArray[0]?.label && valueArray[0].label !== "") {
      return isMulti ? valueArray : valueArray[0];
    }

    // For edit mode with label
    if (selectedMedicineLabel) {
      const medicineId = typeof valueArray[0] === 'string' ? valueArray[0] : valueArray[0]?.value;
      const valueWithLabel = { value: medicineId, label: selectedMedicineLabel };
      return isMulti ? [valueWithLabel] : valueWithLabel;
    }

    // Find labels from options
    const selectedOptions = valueArray
      .map(v => {
        const medicineId = typeof v === 'string' ? v : v?.value;
        const foundOption = options.find(opt => opt.value === medicineId);
        return foundOption || { value: medicineId, label: '' };
      })
      .filter(opt => opt && opt.value);

    return isMulti ? selectedOptions : selectedOptions[0] || null;
  }, [value, options, isMulti, selectedMedicineLabel]);

  const handleChange = useCallback((selected) => {
    if (onChange) {
      onChange(selected);
    }
  }, [onChange]);

  // Custom styles to ensure dropdown appears above all elements on the page
  const customStyles = {
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  return (
    <Select
      isMulti={isMulti}
      options={options}
      value={selectedValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      placeholder="Search & Select Medicine"
      isLoading={loading}
      isSearchable
      isClearable
      onMenuScrollToBottom={handleMenuScrollToBottom}
      noOptionsMessage={() => loading ? "Loading..." : "No medicines found"}
      formatOptionLabel={(option) => option.label}
      getOptionLabel={(option) => option.label}
      getOptionValue={(option) => option.value}
      maxMenuHeight={300}
      styles={customStyles}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
    />
  );
};

export default MedicineDropdown;
